import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  Heart, 
  LayoutDashboard, 
  Brain, 
  Calendar, 
  Activity, 
  Utensils, 
  FileText, 
  LogOut, 
  MessageCircle, 
  TrendingUp,
  Menu,
  X,
  User,
  Scan,
  ChefHat,
  Pill,
  BarChart3,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Camera,
  Globe,
  Watch,
  Leaf,
  LineChart,
  FlaskConical,
  Settings,
  Umbrella,
  Lock,
  Bot,
  Baby,
  Users,
  UserCog,
  Syringe,
  PersonStanding,
  HeartPulse,
  AlertTriangle,
  ScanLine,
  MapPin,
  HeartHandshake,
  Dna,
  Trophy,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import secureStorage from '../utils/secureStorage';

const Navigation = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [menuConfig, setMenuConfig] = useState(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'te', name: 'తెలుగు', flag: '🇮🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];

  // Fetch menu configuration on mount
  useEffect(() => {
    const fetchMenuConfig = async () => {
      try {
        const response = await axios.get(`${API}/menu-config`);
        setMenuConfig(response.data.menu_items);
      } catch (error) {
        process.env.NODE_ENV === 'development' && console.log('Using default menu config');
        setMenuConfig(null);
      }
    };
    
    if (user) {
      fetchMenuConfig();
    }
  }, [user]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setShowLanguageMenu(false);
    // Set RTL for Arabic
    if (lng === 'ar') {
      document.documentElement.dir = 'rtl';
    } else {
      document.documentElement.dir = 'ltr';
    }
  };

  const handleLogout = () => {
    // Clear all user data
    secureStorage.clearToken();
    localStorage.removeItem('user');
    
    // Close mobile menu if open
    setIsMobileMenuOpen(false);
    
    // Clear user state
    setUser(null);
    
    // Navigate to home page
    navigate('/', { replace: true });
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Icon mapping for dynamic menu items - must match icons defined in server.py DEFAULT_MENU_ITEMS
  const ICON_MAP = {
    LayoutDashboard, BarChart3, Shield, ShieldCheck, ShieldAlert, Camera, Watch, FlaskConical, 
    LineChart, Leaf, FileText, Calendar, Pill, Brain, Activity, 
    Scan, MessageCircle, TrendingUp, Utensils, Settings, Umbrella, Lock, Bot, Baby, Users,
    PersonStanding, HeartPulse, Syringe, AlertTriangle, ScanLine, MapPin, HeartHandshake, Dna, Trophy, Sparkles
  };

  // Default nav items
  const defaultNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), color: 'from-blue-500 to-indigo-500', roles: ['patient', 'doctor', 'super_admin'] },
    { path: '/chatbot', icon: Bot, label: 'AI Health Assistant', color: 'from-violet-500 to-purple-600', roles: ['patient', 'doctor'] },
    { path: '/family', icon: Users, label: 'Family Health', color: 'from-blue-500 to-indigo-600', roles: ['patient'] },
    { path: '/elderly-care', icon: PersonStanding, label: 'Elderly Care', color: 'from-amber-500 to-orange-600', roles: ['patient'] },
    { path: '/pregnancy', icon: Baby, label: 'Pregnancy', color: 'from-pink-500 to-rose-500', roles: ['patient'] },
    { path: '/vaccination', icon: Syringe, label: 'Vaccination', color: 'from-teal-500 to-cyan-500', roles: ['patient'] },
    { path: '/emergency-sos', icon: AlertTriangle, label: 'Emergency SOS', color: 'from-red-600 to-rose-600', roles: ['patient'] },
    { path: '/medicine-scanner', icon: ScanLine, label: 'Medicine Scanner', color: 'from-purple-500 to-violet-600', roles: ['patient'] },
    { path: '/nearby-clinics', icon: MapPin, label: 'Nearby Clinics', color: 'from-blue-500 to-cyan-500', roles: ['patient'] },
    { path: '/first-aid', icon: HeartHandshake, label: 'First Aid Guide', color: 'from-teal-500 to-emerald-500', roles: ['patient'] },
    { path: '/analytics', icon: BarChart3, label: t('nav.analytics'), roles: ['super_admin'], color: 'from-orange-500 to-amber-500' },
    { path: '/emergency-profile', icon: Shield, label: t('nav.emergencyProfile'), roles: ['patient'], color: 'from-red-500 to-rose-500' },
    { path: '/meal-analyzer', icon: Camera, label: t('nav.mealAnalyzer'), roles: ['patient'], color: 'from-amber-500 to-orange-500' },
    { path: '/wearables', icon: Watch, label: 'Wearables Hub', roles: ['patient'], color: 'from-cyan-500 to-blue-500' },
    { path: '/genetic-health', icon: Dna, label: 'Genetic Health Map', roles: ['patient'], color: 'from-purple-500 to-pink-600' },
    { path: '/healthbet', icon: Trophy, label: 'HealthBet', roles: ['patient'], color: 'from-yellow-500 to-orange-600' },
    { path: '/health-predictions', icon: Sparkles, label: 'Health Predictions', roles: ['patient'], color: 'from-purple-500 to-pink-600' },
    { path: '/insurance', icon: Umbrella, label: 'Insurance Hub', roles: ['patient'], color: 'from-emerald-500 to-teal-600' },
    { path: '/hipaa', icon: Lock, label: 'Privacy & Security', roles: ['patient', 'doctor', 'super_admin'], color: 'from-indigo-600 to-purple-600' },
    { path: '/diagnostics', icon: FlaskConical, label: 'Book Diagnostics', roles: ['patient'], color: 'from-yellow-500 to-orange-500' },
    { path: '/lab-comparator', icon: LineChart, label: 'Lab Comparator', roles: ['patient'], color: 'from-emerald-500 to-teal-500' },
    { path: '/yoga-exercise', icon: Leaf, label: 'Yoga & Exercise', roles: ['patient'], color: 'from-purple-500 to-pink-500' },
    { path: '/reports', icon: FileText, label: t('nav.reports'), roles: ['patient'], color: 'from-slate-500 to-gray-600' },
    { path: '/appointments', icon: Calendar, label: t('nav.appointments'), color: 'from-green-500 to-emerald-500', roles: ['patient', 'doctor'] },
    { path: '/medications', icon: Pill, label: t('nav.medications'), roles: ['patient'], color: 'from-pink-500 to-rose-500' },
    { path: '/ai-features', icon: Brain, label: t('nav.aiFeatures'), roles: ['patient'], color: 'from-violet-600 to-purple-600' },
    { path: '/chronic-disease', icon: Activity, label: t('nav.chronicDisease'), roles: ['patient'], color: 'from-rose-500 to-red-500' },
    { path: '/retinal-scan', icon: Scan, label: t('nav.retinalScan'), roles: ['patient'], color: 'from-sky-500 to-blue-500' },
    { path: '/voice-biomarker', icon: MessageCircle, label: t('nav.voiceAnalysis'), roles: ['patient'], color: 'from-indigo-500 to-violet-500' },
    { path: '/predictive-health', icon: TrendingUp, label: t('nav.predictiveHealth'), roles: ['patient'], color: 'from-teal-500 to-cyan-500' },
    { path: '/diet-tracker', icon: Utensils, label: t('nav.dietTracker'), roles: ['patient'], color: 'from-lime-500 to-green-500' },
    { path: '/patients', icon: LayoutDashboard, label: t('nav.patientManagement'), roles: ['doctor'], color: 'from-blue-500 to-cyan-500' },
    { path: '/ai-prescription', icon: FileText, label: t('nav.aiPrescription'), roles: ['doctor'], color: 'from-teal-500 to-emerald-500' },
  ];

  // Build navItems from menuConfig or use defaults
  const navItems = menuConfig 
    ? menuConfig.map(item => {
        const defaultItem = defaultNavItems.find(d => d.path === item.path);
        return {
          path: item.path,
          icon: ICON_MAP[item.icon] || LayoutDashboard,
          label: item.label,
          color: defaultItem?.color || 'from-gray-500 to-gray-600',
          roles: defaultItem?.roles
        };
      })
    : defaultNavItems;
  
  // Add Menu Settings for super_admin
  const adminNavItems = user?.role === 'super_admin' ? [
    ...navItems,
    { path: '/admin/menu-settings', icon: Settings, label: 'Menu Settings', color: 'from-purple-500 to-indigo-500', roles: ['super_admin'] }
  ] : navItems;

  // Debug: Log user role on component mount
  useEffect(() => {
    process.env.NODE_ENV === 'development' && console.log('Navigation Debug:', {
      userRole: user?.role,
      userEmail: user?.email,
      analyticsItem: navItems.find(item => item.path === '/analytics')
    });
  }, [user]);

  return (
    <TooltipProvider delayDuration={200}>
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-[100] shadow-sm safe-area-top">
      <div className="container mx-auto px-3 sm:px-4 lg:px-4 max-w-full">
        <div className="flex justify-between items-center h-16 gap-2">
          {/* Logo - Always visible */}
          <Link to="/dashboard" className="flex items-center gap-2 flex-shrink-0 min-w-0 relative z-10">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
              <Heart className="w-4 h-4 sm:w-6 sm:h-6 text-white" fill="white" />
            </div>
            <span className="text-base sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent truncate">
              HealthAI
            </span>
          </Link>

          {/* Desktop Navigation - Colorful icons with tooltips */}
          <div className="hidden lg:flex items-center gap-1.5 flex-1 justify-center mx-4 relative z-10">
            <div className="flex items-center gap-1.5">
              {adminNavItems.map((item) => {
                // Check if user should see this item
                const shouldShow = !item.roles || // No role restriction, show to everyone
                                   user.role === 'super_admin' || // Super admin sees all
                                   item.roles.includes(user.role); // User's role is in allowed roles
                
                if (!shouldShow) {
                  return null;
                }
                
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Tooltip key={item.path}>
                    <TooltipTrigger asChild>
                      <Link
                        to={item.path}
                        className="group"
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                          isActive
                            ? `bg-gradient-to-br ${item.color} shadow-lg ring-2 ring-offset-2 ring-white/50 scale-110`
                            : `bg-gradient-to-br ${item.color} opacity-70 hover:opacity-100 hover:scale-110 hover:shadow-lg`
                        }`}>
                          <Icon className={`w-5 h-5 text-white transition-transform duration-300 ${!isActive ? 'group-hover:scale-110' : ''}`} />
                        </div>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className={`bg-gradient-to-r ${item.color} text-white font-medium px-3 py-1.5`}>
                      {item.label}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Desktop User Info & Logout */}
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0 relative z-10 ml-2">
            {/* Language Switcher */}
            <div className="relative">
              <Button
                onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 hover:bg-blue-50 hover:border-blue-200 flex-shrink-0 px-3 py-1.5 h-auto"
              >
                <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-xs">{languages.find(l => l.code === i18n.language)?.flag || '🌐'}</span>
              </Button>
              {showLanguageMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center gap-2 ${
                        i18n.language === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex flex-col items-end gap-1">
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 hover:bg-red-50 hover:border-red-200 hover:text-red-600 flex-shrink-0 px-3 py-1.5 h-auto"
                data-testid="logout-btn"
              >
                <LogOut className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="text-xs">{t('nav.logout')}</span>
              </Button>
              <div className="flex items-center gap-1.5 px-2 py-0.5">
                <User className="w-3 h-3 text-teal-600 flex-shrink-0" />
                <div className="text-right">
                  <p className="text-[11px] font-medium text-gray-700 leading-tight">{user?.full_name}</p>
                  <p className="text-[9px] text-gray-500 capitalize leading-tight">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Menu Button & User Info */}
          <div className="flex lg:hidden items-center justify-end gap-2 min-w-0 relative z-10">
            {/* Mobile User Info - Very compact */}
            <div className="flex items-center gap-1 px-2 py-1 bg-teal-50 rounded-full border border-teal-200 min-w-0 max-w-[120px] sm:max-w-none flex-shrink-0">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center flex-shrink-0">
                <User className="w-3 h-3 text-white" />
              </div>
              <div className="hidden xs:block min-w-0 pr-1">
                <p className="text-xs font-semibold text-gray-800 truncate max-w-12 leading-tight">{user?.full_name?.split(' ')[0]}</p>
              </div>
            </div>

            {/* Quick Logout Button for small screens */}
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="sm:hidden p-1.5 min-w-[36px] h-9 hover:bg-red-50 hover:border-red-200 border-gray-300 flex-shrink-0"
              title="Logout"
            >
              <LogOut className="w-4 h-4 text-red-600 flex-shrink-0" />
            </Button>

            {/* Mobile Menu Trigger */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="p-2 min-w-[40px] h-9 hover:bg-teal-50 hover:border-teal-200 border-teal-300 flex-shrink-0"
                  aria-label="Open navigation menu"
                >
                  <Menu className="w-4 h-4 text-teal-700 flex-shrink-0" />
                </Button>
              </SheetTrigger>
              
              <SheetContent 
                side="right" 
                className="w-[300px] sm:w-[350px] bg-white border-l border-gray-200 flex flex-col h-full"
              >
                <SheetHeader className="border-b border-gray-100 pb-4 mb-4 flex-shrink-0">
                  <SheetTitle className="text-left flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                      <Heart className="w-6 h-6 text-white" fill="white" />
                    </div>
                    <div>
                      <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                        HealthAI
                      </span>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                {/* Scrollable Content Container */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden -mx-6 px-6">
                  {/* Mobile User Info */}
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-xl mb-6 border border-teal-100">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
                        <User className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">{user?.full_name}</p>
                        <p className="text-sm text-gray-600 capitalize">{user?.role}</p>
                        <p className="text-xs text-teal-600">{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Navigation Links */}
                  <div className="space-y-2 mb-6">
                  {adminNavItems.map((item) => {
                    // Check if user should see this item
                    const shouldShow = !item.roles || // No role restriction, show to everyone
                                       user.role === 'super_admin' || // Super admin sees all
                                       item.roles.includes(user.role); // User's role is in allowed roles
                    
                    if (!shouldShow) {
                      return null;
                    }
                    
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                          isActive
                            ? 'bg-gray-100 shadow-sm'
                            : 'hover:bg-gray-50'
                        }`}
                        data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${item.color} ${isActive ? 'ring-2 ring-offset-1 ring-gray-300' : ''}`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <span className={`text-sm ${isActive ? 'text-gray-900 font-semibold' : 'text-gray-700'}`}>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>

                {/* Mobile Logout Button */}
                <div className="border-t border-gray-100 pt-4 flex-shrink-0 mt-auto">
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2 py-3 hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors"
                    data-testid="logout-btn-mobile"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Logout</span>
                  </Button>
                </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
    </TooltipProvider>
  );
};

export default Navigation;
